/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useRouter as useCompatibleRouter } from 'next/compat/router';
import { usePathname, useRouter } from 'next/navigation';
import type { NextRouter } from 'next/router'; // eslint-disable-line no-restricted-imports
import { type BaseRouter } from 'next/dist/shared/lib/router/router';

const emptyObject = {};

export type LegacyRouter = BaseRouter & {
  /**
   * Legacy router.push(). Allows shallow routing as the new router does not.
   *
   * @deprecated Prefer `useRouter` from `next/navigation`
   */
  push: NextRouter['push'] | null;
  /**
   * Legacy router.replace(). Allows shallow routing as the new router does not.
   *
   * @deprecated Prefer `useRouter` from `next/navigation`
   */
  replace: NextRouter['replace'] | null;
  /**
   * @deprecated
   * @see [Router Events](https://nextjs.org/docs/app/api-reference/functions/use-router#router-events)
   */
  events: NextRouter['events'] | null;
  /**
   * @deprecated
   */
  isReady: boolean;
};

/**
 * Provides backwards compatibility with the Pages Router API during migration to App Router.
 * Attempts to use the compatibility layer from `next/compat/router`, falling back to a
 * shim built from App Router hooks if unavailable.
 *
 * @returns {LegacyRouter} A router object compatible with the legacy Pages Router API
 *
 * @deprecated Use the corresponding hooks from `next/navigation`.
 * @see [useRouter](https://nextjs.org/docs/app/api-reference/functions/use-router)
 * @see [useParams](https://nextjs.org/docs/app/api-reference/functions/use-params)
 * @see [usePathname](https://nextjs.org/docs/app/api-reference/functions/use-pathname)
 * @see [useSearchParams](https://nextjs.org/docs/app/api-reference/functions/use-search-params)
 */
export const useLegacyRouter = (): LegacyRouter => {
  const legacyRouter = useCompatibleRouter();
  const router = useRouter();
  const pathname = usePathname();

  if (legacyRouter) {
    return legacyRouter;
  }

  return {
    ...router,
    route: '',
    asPath: pathname,
    pathname,
    query: emptyObject,
    basePath: '',
    isLocaleDomain: false,
    push: null,
    replace: null,
    events: null,
    isReady: true,
  };
};
